<template>
  <div class="article-page">
    <div class="container">
      <BaseBreadcrumbs :items="breadcrumbs" />

      <div class="article-header">
        <h1 class="label">Журнал</h1>
        <ArticleSearchWithAutocomplete
          v-model="searchValue"
          label="Поиск по новостям"
          :suggestions="searchSuggestions"
          @search="handleSearch"
          @suggestion-select="onSuggestionSelect"
          @search-change="onSearchChange"
        />
      </div>

      <ClientOnly>
        <button v-if="!isDesktop && !isDrawerOpen" class="button-filter-fab" @click="openDrawer">
          <BaseButton
            class="button-filter"
            icon-left="essential/filter"
            :size="isMobile ? 'l' : 'xl'"
          />
          <span v-if="hasActiveFilters" class="indicator" />
        </button>
      </ClientOnly>

      <ArticleSidebarLayout>
        <template #sidebar>
          <ArticleSidebarFilters
            v-if="filters"
            :filters="filters"
            :initial-rubric-id="queryRubricId"
            :initial-tag-ids="queryTagIds"
            @on-change="onChangeFilters"
          />
        </template>

        <ArticleAudienceTabs
          v-if="isDesktop"
          :model-value="activeAudience"
          :items="audienceTabs"
          @update:model-value="handleAudienceChange"
        />

        <div class="main-content">
          <ArticleFilteredResults v-if="hasActiveFilters" :loadable="newsLoadable" />

          <ArticleSectionsFeed
            v-else-if="sections?.length"
            :sections="sections"
            @view-all="onViewAll"
          />
        </div>
      </ArticleSidebarLayout>

      <ClientOnly>
        <ArticleFilterDrawer
          v-if="!isDesktop && isDrawerOpen && filters"
          @close="closeDrawer"
          @clear="clearFilters"
        >
          <ArticleSidebarFilters
            :filters="filters"
            :initial-rubric-id="queryRubricId"
            :initial-tag-ids="queryTagIds"
            show-titles
            @on-change="onChangeFilters"
          />
          <div class="filter-card audience-filter-card">
            <div class="audience-title">Аудитории</div>
            <ArticleAudienceTabs
              v-if="audienceTabs.length > 1"
              :model-value="activeAudience"
              :items="audienceTabs"
              @update:model-value="handleAudienceChange"
            />
          </div>
        </ArticleFilterDrawer>
      </ClientOnly>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseButton } from '~/components/base-ui/buttons'
  import { BaseBreadcrumbs } from '~/components/base-ui/breadcrumbs'
  import {
    ArticleSearchWithAutocomplete,
    ArticleSidebarFilters,
    ArticleSidebarLayout,
    ArticleSectionsFeed,
    ArticleFilteredResults,
    ArticleFilterDrawer,
    ArticleAudienceTabs,
  } from '~/components/article/article-main'
  import { useScrollLock } from '@vueuse/core'
  import { useArticleFilters } from '~/composables/articles/use-article-filters'
  import { toLoadable } from '~/shared/utils/loadable'
  import type { SearchSuggestion } from '~/components/article/article-main/ArticleSearchWithAutocomplete.vue'

  const route = useRoute()
  const router = useRouter()
  const { $services } = useNuxtApp()
  const { isDesktop, isMobile } = useBreakpoint()

  const isDrawerOpen = ref(false)
  const scrollLock = import.meta.client ? useScrollLock(document.body) : ref(false)

  function openDrawer() {
    isDrawerOpen.value = true
    scrollLock.value = true
  }

  function closeDrawer() {
    isDrawerOpen.value = false
    scrollLock.value = false
  }

  onUnmounted(() => {
    if (isDrawerOpen.value) closeDrawer()
  })

  const {
    filters,
    activeAudience,
    searchValue,
    hasActiveFilters,
    audienceTabs,
    buildFilterParams,
    loadFilters,
    onChangeFilters,
    handleAudienceChange,
    handleSearch,
    clearFilters,
    viewAllCategory,
  } = await useArticleFilters()

  const queryRubricId = computed(() => String(route.query.rubric_id ?? ''))
  const queryTagIds = computed(() => String(route.query.tags_id ?? ''))
  const searchSuggestions = ref<SearchSuggestion[]>([])

  const { breadcrumbs } = await useBreadcrumbs()

  const { data: sections } = await useAsyncData('article-main-page', () =>
    $services.articleService.main.getMainPage()
  )

  const {
    data: newsResult,
    status: newsStatus,
    execute: executeNewsQuery,
  } = await useAsyncData(
    'article-news-list',
    () =>
      $services.articleService.main.getNewsList({
        ...buildFilterParams(),
        'page[number]': Number(route.query.p) || 1,
      }),
    { immediate: hasActiveFilters.value }
  )

  const newsLoadable = toLoadable(
    computed(() => newsResult.value ?? null),
    newsStatus
  )

  watch(
    () => route.query,
    () => {
      loadFilters()
      if (hasActiveFilters.value) executeNewsQuery()
    }
  )

  function onViewAll(categoryId: number) {
    viewAllCategory(categoryId)
    if (import.meta.client) nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
  }

  async function onSearchChange(query: string) {
    if (query.length < 2) {
      searchSuggestions.value = []
      return
    }
    try {
      const res = await $services.articleService.main.getNewsList({
        'filter[search]': query,
        'page[size]': 5,
      })
      searchSuggestions.value = res.items as SearchSuggestion[]
    } catch {
      searchSuggestions.value = []
    }
  }

  function onSuggestionSelect(suggestion: SearchSuggestion) {
    router.push(`/news/${suggestion.slug}`)
  }

  useSeoMeta({ title: 'Журнал', description: 'Новости для студентов в сфере АПК' })
</script>

<style lang="scss" scoped>
  .article-page {
    @extend %flex-column;

    width: 100%;
    padding-bottom: 64px;

    @include vp-767 {
      padding-bottom: 40px;
    }

    > .container {
      @include container;
      @extend %flex-column;

      padding-top: 32px;

      @include vp-767 {
        padding-top: 24px;
      }
    }
  }

  .article-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
    margin: 32px 0 16px;
    padding: 32px;
    background: $color-default-white;
    border-radius: 24px;

    @include vp-767 {
      gap: 12px;
      padding: 16px;
      margin: 16px 0;
    }

    > .label {
      font-size: 32px;
      font-weight: 700;
      line-height: 31px;
      margin: 0;
      flex-shrink: 0;

      @include vp-767 {
        font-size: 22px;
        line-height: 26px;
      }
    }
  }

  .button-filter-fab {
    position: fixed;
    bottom: 50vh;
    right: 20px;
    z-index: 10;
    cursor: pointer;
    display: none;
    background: transparent;
    border: 0;
    padding: 0;

    @include vp-1279 {
      display: block;
    }

    @include vp-767 {
      right: 16px;
    }

    > .indicator {
      position: absolute;
      top: 8px;
      right: 12px;
      width: 12px;
      height: 12px;
      background-color: $color-banana-50;
      border-radius: 50%;

      @include vp-767 {
        top: 6px;
        right: 8px;
        width: 10px;
        height: 10px;
      }
    }
  }

  .main-content {
    min-height: 400px;
  }

  .filter-card {
    background: $color-default-white;
    border-radius: 20px;
    padding: 24px 20px;
    box-shadow: 0 2px 12px 0 rgb(0 0 0 / 4%);

    @include vp-767 {
      border-radius: 16px;
      padding: 12px 8px;
      box-shadow: none;
    }
  }

  .audience-filter-card {
    > .audience-title {
      font-size: 20px;
      font-weight: 500;
      margin-bottom: 8px;

      @include vp-767 {
        font-size: 16px;
      }
    }

    :deep(.article-audience-tabs) {
      margin-bottom: 0;
    }
  }
</style>
